
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {restDownloadFile} from "@/utils/rest"
import {theme} from "@/theme"
import Axios, {AxiosError, AxiosResponse} from "axios"
import Message from "@/components/Message.vue"
import {format} from "date-fns"

interface ImportFile {
  name: string
  filename: string
  status: string
  modified: string
  dataDate: string
}

interface ImportErrorLog {
  filename: string
  line: number
  timestamp: string
  message: string
}

@Component({
  components: {
    Message,
    PageHeader,
    HelpIcon,
  },
})
export default class OneCentralImportView extends Vue {
  t = theme()

  readonly apiPath = "/api/devops/onecentral/imports"

  readonly visibleFileColumns = ["filename", "status", "modified", "dataDate"]
  readonly visibleErrorColumns = ["filename", "line", "timestamp", "message"]
  readonly queueableStatuses = ["Failed", "Pending", "Complete"]
  readonly deletableStatuses = ["Failed", "Pending", "Unknown", "Queued", "Exported"]

  requestInProgress = false

  ocImports: Array<ImportFile> = new Array<ImportFile>()
  ocImportsSelected: Array<ImportFile> = new Array<ImportFile>()
  ocImportsErrorLogs: Array<ImportErrorLog> = new Array<ImportErrorLog>()
  ocImportsMessage: string | null = null
  ocImportsError = false
  ocImportsInProgress = false
  ocImportsShowCompleted = true

  async created() {
    await this.getOcImportFiles()
    await this.getOcImportErrorLogs()
  }

  private async refresh(done: () => void) {
    await this.getOcImportFiles()
    await this.getOcImportErrorLogs()
    done()
  }

  private async getOcImportFiles() {
    this.ocImportsInProgress = true
    await Axios.get(this.apiPath)
      .then((response: AxiosResponse) => {
        this.ocImportsMessage = null
        this.ocImports = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.ocImportsError = true
        this.ocImportsMessage = reason.response?.data.message
      })
      .finally(() => {
        this.ocImportsInProgress = false
        this.ocImportsSelected = []
      })
  }

  private async getOcImportErrorLogs() {
    await Axios.get(`${this.apiPath}/errorLogs`).then((response: AxiosResponse) => {
      this.ocImportsErrorLogs = response.data
    })
  }

  get ocImportFiles(): Array<ImportFile> {
    return this.ocImports.filter(file => this.ocImportsShowCompleted || file.status !== "Complete")
  }

  get isOneSelected(): boolean {
    return this.ocImportsSelected.length === 1
  }

  get isQueueable(): boolean {
    return (
      this.isOneSelected &&
      this.queueableStatuses.includes(this.ocImportsSelected.find(Boolean)!.status)
    )
  }

  get isDeletable(): boolean {
    return (
      this.isOneSelected &&
      this.deletableStatuses.includes(this.ocImportsSelected.find(Boolean)!.status)
    )
  }

  private async downloadFile() {
    if (this.isOneSelected) {
      this.requestInProgress = true
      const file = this.ocImportsSelected.find(Boolean)!
      const parameters = {filename: file.name}
      await restDownloadFile(`${this.apiPath}/download`, file.filename, parameters)
      this.requestInProgress = false
    }
  }

  private async queueFile() {
    if (this.isQueueable) {
      this.requestInProgress = true
      const file = this.ocImportsSelected.find(Boolean)!
      const form = new FormData()
      form.set("filename", file.name)
      void Axios.patch(`${this.apiPath}/queue`, form)
        .then(this.getOcImportFiles)
        .catch((reason: AxiosError<any>) => {
          this.ocImportsError = true
          this.ocImportsMessage = reason.response?.data.message
        })
        .finally(() => (this.requestInProgress = false))
    }
  }

  private async deleteFile() {
    if (this.isDeletable) {
      this.requestInProgress = true
      const file = this.ocImportsSelected.find(Boolean)!
      const config = {params: {filename: file.name}}
      void Axios.delete(`${this.apiPath}/delete`, config)
        .then(this.getOcImportFiles)
        .catch((reason: AxiosError<any>) => {
          this.ocImportsError = true
          this.ocImportsMessage = reason.response?.data.message
        })
        .finally(() => (this.requestInProgress = false))
    }
  }

  private async downloadExceptions() {
    this.requestInProgress = true
    const now = format(new Date(), "ddMMyyyy")
    await restDownloadFile(`/api/devops/onecentral/imports/report`, `Exceptions - ${now}.xlsx`)
    this.requestInProgress = false
  }
}
